import { message } from 'antd';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addGiide, setGiideListSortType, setGiides } from '../../actions';
import { ListGiidesQuery, PublishState } from '../../graphql/api';
import { Giide, IState } from '../../graphql/types';
import {
  sortAlphabeticallyByKey,
  sortAlphabeticallyByName,
  sortUpdatedAt,
} from '../../components/utils';
import { Filters, giideListSort } from '../../types';
import useUser from '../useUser';

const listGiides = gql`
  query ListGiides($filter: ModelGiideFilterInput) {
    listGiides(filter: $filter, limit: 2000) {
      items {
        id
        title
        owner
        createdAt
        updatedAt
        publishedAt
        publishState
        exportVideoState
        publishedWithTranscript
        unauthenticatedAccessAllowed
        passwordEnabled
        cardDetails {
          cardColor
          cardShade
          thumbnailImg
          thumbnailCustomName
          thumbnailCustomPath
          thumbnailImageService
          useCustomThumbnail
        }
        isInGiideStudioLibrary
        speaker {
          firstName
          lastName
          fullName
        }
      }
    }
  }
`;

const useGiideList = (noFetch?: boolean) => {
  const dispatch = useDispatch();
  const data = useSelector<IState, Giide[] | undefined>(state => state.giides);
  const fetching = useSelector<IState, boolean>(
    state => !!state.giideListFetching
  );
  const [error, setError] = React.useState<boolean>(false);
  const giideListFilter = useSelector<IState, Filters | undefined>(
    state => state.giideListFilter
  );
  const giideListSearch = useSelector<IState, string | undefined>(
    state => state.giideListSearch
  );
  const giideListSortType = useSelector<IState, giideListSort | undefined>(
    state => state.giideListSortType
  );
  const giideListViewType = useSelector<IState, string | undefined>(
    state => state.giideListViewType
  );
  const { user } = useUser();

  const giideCount = useMemo<number>(() => {
    return data?.length || 0;
  }, [data]);

  const refetch = React.useCallback(
    (latest?: boolean) => {
      if (user) {
        (
          API.graphql(
            graphqlOperation(
              listGiides,
              latest
                ? {
                    filter: {
                      updatedAt: {
                        gt: new Date(Date.now() - 60000).toISOString(),
                      },
                    },
                  }
                : undefined
            )
          ) as Promise<{
            data: ListGiidesQuery;
          }>
        )
          .then(result => {
            if (latest) {
              result.data.listGiides!.items?.forEach(g =>
                dispatch(addGiide(g as Giide))
              );
            } else {
              dispatch(
                setGiides(
                  result.data.listGiides!.items?.filter(
                    giide => !giide?.isInGiideStudioLibrary
                  )
                )
              );
            }
          })
          .catch(() => {
            message.error('Failed to fetch Giides');
            setError(true);
          });
      }
    },
    [dispatch, user]
  );

  const filteredData = React.useMemo(() => {
    let dataFiltered = data || [];
    if (giideListFilter && dataFiltered) {
      const filterNotSelected = Object.keys(giideListFilter).every(
        k => !giideListFilter[k]
      );
      if (!filterNotSelected) {
        dataFiltered = dataFiltered.filter(giide => {
          return (
            (giideListFilter.published &&
              giide.publishState === PublishState.published) ||
            (giideListFilter.unpublished &&
              giide.publishState === PublishState.unpublished) ||
            //(giideListFilter.pending && giide.publishState === 'pending') || // not used for now
            // (giideListFilter.error &&
            //   giide.publishState === PublishState.error) ||
            //(giideListFilter.waiting && giide.publishState === 'waiting') || // not used for now
            // (giideListFilter.migrating &&
            //   giide.publishState === PublishState.migrating) ||
            // (giideListFilter.transcoding &&
            //   giide.publishState === PublishState.transcoding) ||
            // (giideListFilter.review &&
            //   giide.publishState === PublishState.review) ||
            (giideListFilter.public && giide.unauthenticatedAccessAllowed) ||
            (giideListFilter.private && !giide.unauthenticatedAccessAllowed)
          );
        });
      }
      if (giideListSearch) {
        dataFiltered = dataFiltered.filter(giide =>
          giide.title.toLowerCase().includes(giideListSearch.toLowerCase())
        );
      }
    }
    return dataFiltered.sort(sortUpdatedAt);
  }, [giideListFilter, data, giideListSearch]);

  const sortedData = React.useMemo(() => {
    if (filteredData && giideListSortType) {
      let dataSorted = [...filteredData];
      switch (giideListSortType.type) {
        case 'Title':
          dataSorted = dataSorted.sort(sortAlphabeticallyByKey('title'));
          break;
        case 'Author':
          dataSorted = !giideListSortType.descending
            ? dataSorted.sort(sortUpdatedAt).sort(sortAlphabeticallyByName)
            : dataSorted
                .sort(sortUpdatedAt)
                .reverse()
                .sort(sortAlphabeticallyByName);
          break;
        case 'Status':
          dataSorted = dataSorted.sort(sortAlphabeticallyByKey('publishState'));
          break;
        default:
          dataSorted = dataSorted.sort(sortUpdatedAt);
      }
      return !giideListSortType.descending ? dataSorted : dataSorted.reverse();
    }
  }, [filteredData, giideListSortType]);

  const handleSortChange = React.useCallback(
    type => {
      giideListSortType?.type === type
        ? dispatch(
            setGiideListSortType({
              type: type,
              descending: !giideListSortType?.descending,
            })
          )
        : dispatch(setGiideListSortType({ type: type, descending: false }));
    },
    [giideListSortType, dispatch]
  );

  React.useEffect(() => {
    if (!data && !fetching && !noFetch && !error) {
      refetch();
    }
  }, [refetch, data, fetching, dispatch, noFetch, error]);

  return {
    refetch,
    data,
    filteredData,
    giideListFilter,
    sortedData,
    giideListSearch,
    giideListSortType,
    giideListViewType,
    handleSortChange,
    giideCount,
  };
};

export default useGiideList;
